import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { TreeSelect, Spin } from 'antd';
import { nullSafeGet } from '../../utils/DataAccessUtils';

interface SubAssetMultiSelectProps {
	parentAssetId?: number | null;
	value?: number[];
	onChange?: (ids: number[]) => void;
	disabled?: boolean;
	placeholder?: string;
	fetchSubAssets: (parentId: number) => Promise<any[]>;
}

const SubAssetMultiSelect: FC<SubAssetMultiSelectProps> = ({
	parentAssetId,
	value,
	onChange,
	disabled,
	placeholder = 'Select sub assets',
	fetchSubAssets,
}) => {
	const [subAssets, setSubAssets] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const lastFetchedParent = useRef<number | null | undefined>(undefined);

	useEffect(() => {
		let cancelled = false;
		if (!parentAssetId) {
			setSubAssets([]);
			lastFetchedParent.current = parentAssetId;
			return undefined;
		}
		if (
			lastFetchedParent.current !== undefined &&
			lastFetchedParent.current !== parentAssetId &&
			onChange
		) {
			onChange([]);
		}
		lastFetchedParent.current = parentAssetId;
		setLoading(true);
		fetchSubAssets(parentAssetId)
			.then((records = []) => {
				if (!cancelled) setSubAssets(records);
			})
			.catch(() => {
				if (!cancelled) setSubAssets([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentAssetId]);

	const treeData = useMemo(
		() =>
			subAssets.map((asset) => {
				const id = nullSafeGet('id', asset);
				const name = nullSafeGet('name', asset) || '(unnamed)';
				const assetNumber = nullSafeGet('assetNumber', asset);
				return {
					value: id,
					key: id,
					title: assetNumber ? `${name} · #${assetNumber}` : name,
				};
			}),
		[subAssets]
	);

	return (
		<TreeSelect
			multiple
			treeCheckable
			showCheckedStrategy={TreeSelect.SHOW_PARENT}
			treeDefaultExpandAll
			value={value || []}
			onChange={(ids) => onChange && onChange(ids as number[])}
			placeholder={placeholder}
			disabled={disabled}
			loading={loading}
			notFoundContent={loading ? <Spin size="small" /> : null}
			treeData={treeData}
			treeNodeFilterProp="title"
			showSearch
			style={{ maxWidth: 800, width: '100%' }}
		/>
	);
};

export default SubAssetMultiSelect;
